import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Space, Button, message, Upload, Modal } from 'antd';
import {
  PlusOutlined,
  ArrowLeftOutlined,
  FormOutlined,
  DownloadOutlined,
  UploadOutlined
} from '@ant-design/icons';
import ListItemsDataTable from '../../components/crud/ListItemsDataTable.component';
import DynamicFormSP from '../../components/crud/DynamicFormSP.component';
import { fieldEntityActionConfig } from '../../components/crud/utils/fieldEntityActionConfig';
import PageWrapper from '../../components/common/PageWrapper';
import { exportSystemTemplates, importSystemTemplates } from '../../actions/templateActions';
import {
  SYSTEM_TEMPLATE_CREATE_RESET,
  SYSTEM_TEMPLATE_UPDATE_RESET,
} from '../../constants/templateConstants';
import defaultSystemTemplateConfig from '../../config/defaultSystemTemplateConfig.json';

const normalizeTemplatesForImport = (rows) =>
  rows.map((row) => ({
    name: row.name,
    templateContent: row.templateContent ?? row.content,
    description: row.description,
    type: row.type,
    category: row.category,
    isActive: row.isActive,
  }));

const SystemTemplateManagementScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [isListView, setIsListView] = useState(true);
  const [selectedEntityId, setSelectedEntityId] = useState(null);

  const entityConfig = fieldEntityActionConfig.TemplateIds;
  const [listFilter, setListFilter] = useState({
    query: '',
    page: 1,
    limit: 10,
    filter: entityConfig.primarySearchProp
  });

  const entityListState = useSelector((state) => state.systemTemplateList);
  const { loading, systemTemplates, currentPage, totalPages, totalCount } = entityListState;

  const dtoConfig = useSelector((state) => state.systemTemplateDto);
  const { systemTemplateDto: entityDtoConfig } = dtoConfig;

  const exportState = useSelector((state) => state.systemTemplateExport);
  const importState = useSelector((state) => state.systemTemplateImport);

  useEffect(() => {
    dispatch({ type: SYSTEM_TEMPLATE_CREATE_RESET });
    dispatch({ type: SYSTEM_TEMPLATE_UPDATE_RESET });
  }, [dispatch]);

  useEffect(() => {
    dispatch(entityConfig.listEntities(listFilter));
    dispatch(entityConfig.loadDto());
  }, [dispatch, listFilter]);

  const handleCreate = () => {
    setIsListView(false);
    setSelectedEntityId(null);
  };

  const handleEdit = (id) => {
    setIsListView(false);
    setSelectedEntityId(id);
  };

  const handleDelete = async (id) => {
    await dispatch(entityConfig.deleteEntity(id));
    dispatch(entityConfig.listEntities(listFilter));
  };

  const handleBack = () => {
    setIsListView(true);
    setSelectedEntityId(null);
  };

  const handleExport = async () => {
    try {
      const data = await dispatch(exportSystemTemplates());
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `system-templates-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      message.success(t('Templates exported successfully'));
    } catch (error) {
      message.error(t('Failed to export templates'));
    }
  };

  const handleImport = (file) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const templates = JSON.parse(e.target.result);

        Modal.confirm({
          title: t('Confirm Import'),
          content: t('Are you sure you want to import these templates? This will update existing templates with matching names.'),
          okText: t('Import'),
          cancelText: t('Cancel'),
          onOk: async () => {
            try {
              await dispatch(importSystemTemplates(templates));
              message.success(t('Templates imported successfully'));
              dispatch(entityConfig.listEntities(listFilter));
            } catch (error) {
              message.error(t('Failed to import templates'));
            }
          }
        });
      } catch (error) {
        message.error(t('Invalid JSON file'));
      }
    };
    reader.readAsText(file);
    return false;
  };

  const bundledTemplateDefaults = useMemo(
    () => normalizeTemplatesForImport(defaultSystemTemplateConfig),
    []
  );

  const handleApplyDefaultTemplates = () => {
    Modal.confirm({
      title: t('settings.defaults.confirmTitle', 'Apply bundled defaults?'),
      content: t(
        'settings.defaults.confirmTemplates',
        'This will create or update templates from defaultSystemTemplateConfig.json. Existing templates with the same name will be updated.'
      ),
      okText: t('settings.defaults.applyOk', 'Apply'),
      cancelText: t('Cancel'),
      onOk: async () => {
        try {
          await dispatch(importSystemTemplates(bundledTemplateDefaults));
          message.success(t('settings.defaults.templatesApplied', 'Default templates applied'));
          dispatch(entityConfig.listEntities(listFilter));
        } catch (err) {
          message.error(t('settings.defaults.applyFailed', 'Failed to apply defaults'));
        }
      },
    });
  };

  return (
    <PageWrapper
      title={isListView ? t('System Templates') : (selectedEntityId ? t('Edit Template') : t('Create Template'))}
      subtitle={isListView ? t('Manage system document and email templates') : undefined}
      icon={<FormOutlined />}
    >
      {isListView ? (
        <>
          <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <Button
              type="primary"
              onClick={handleCreate}
              icon={<PlusOutlined />}
            >
              {t('Create Template')}
            </Button>
            <Space>
              <Button
                icon={<DownloadOutlined />}
                onClick={handleExport}
                loading={exportState?.loading}
              >
                {t('Export Templates')}
              </Button>
              <Upload
                accept=".json"
                beforeUpload={handleImport}
                showUploadList={false}
              >
                <Button
                  icon={<UploadOutlined />}
                  loading={importState?.loading}
                >
                  {t('Import Templates')}
                </Button>
              </Upload>
              <Button type="default" onClick={handleApplyDefaultTemplates} loading={importState?.loading}>
                {t('settings.defaults.apply', 'Apply defaults')}
              </Button>
            </Space>
          </Space>
          {entityDtoConfig && (
            <ListItemsDataTable
              loading={loading}
              dtoConfig={entityDtoConfig}
              data={systemTemplates}
              filter={listFilter}
              currentPage={currentPage}
              totalPages={totalPages}
              totalCount={totalCount}
              limit={listFilter.limit}
              onFilterChange={setListFilter}
              onRefresh={() => dispatch(entityConfig.listEntities(listFilter))}
              handleDeleteRow={handleDelete}
              handleEditForm={handleEdit}
              enableActionButtons={true}
            />
          )}
        </>
      ) : (
        <>
          <Button type="text" onClick={handleBack} icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }}>
            {t('Back to Templates List')}
          </Button>
          <DynamicFormSP
            currentEntityId={selectedEntityId}
            defaultEditMode={selectedEntityId ? "Edit" : "Create"}
            primarySearchProp={entityConfig.primarySearchProp}
            secondarySearchProp={entityConfig.secondarySearchProp}
            singularEntity={entityConfig.singularEntity}
            pluralEntity={entityConfig.pluralEntity}
            constantPrefix={entityConfig.constantPrefix}
            loadDto={entityConfig.loadDto}
            createEntity={entityConfig.createEntity}
            updateEntity={entityConfig.updateEntity}
            deleteEntity={entityConfig.deleteEntity}
            getEntityDetails={entityConfig.getEntityDetails}
            listEntities={entityConfig.listEntities}
            listEntitiesByIds={entityConfig.listEntitiesByIds}
            onUpdatedEntityChange={() => { }}
            isParent={true}
            fieldsToHide={["Id"]}
            summaryProps={entityConfig.summaryProps}
            enableCreate={true}
            enableSearchBar={false}
            enableActionButtons={true}
          />
        </>
      )}
    </PageWrapper>
  );
};

export default SystemTemplateManagementScreen;
